import { FunctionComponent, useEffect, useState } from "react";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { showError, showSuccess } from "../common/ToastNotification";
import { AGENCY_FIELD_STATUSES } from "../../api/query";
import { BULK_UPSERT_AGENCY_FIELD_STATUS } from "../../api/mutation";
import Loader from "../common/Loader";

interface AgencyInfoTabProps {
    agencyId: any; // migration record id
}

interface FieldStatus {
    field_id: number;
    field_name: string;
    field_description: string;
    has_issue: boolean;
    is_complete: boolean;
    need_validation: boolean;
    remarks: string;
    status_id: number;
}

interface FieldState {
    field_id: number;
    has_issue: boolean;
    is_complete: boolean;
    need_validation: boolean;
    remarks: string;
}

const AgencyInfoTab: FunctionComponent<AgencyInfoTabProps> = ({ agencyId }) => {
    const [fieldStates, setFieldStates] = useState<Record<number, FieldState>>({});

    const { data, loading } = useQuery(AGENCY_FIELD_STATUSES, {
        variables: { agency_id: parseInt(agencyId) },
        fetchPolicy: "no-cache",
        skip: !agencyId,
    });

    const [bulkUpsert, { loading: saving }] = useMutation(BULK_UPSERT_AGENCY_FIELD_STATUS, {
        fetchPolicy: "no-cache",
    });

    const fields: FieldStatus[] = data?.agency_field_statuses?.field_statuses || [];

    // Pre-fill state from API response
    useEffect(() => {
        if (!fields.length) return;
        const initial: Record<number, FieldState> = {};
        fields.forEach(f => {
            initial[f.field_id] = {
                field_id: f.field_id,
                has_issue: f.has_issue ?? false,
                is_complete: f.is_complete ?? false,
                need_validation: f.need_validation ?? false,
                remarks: f.remarks ?? "",
            };
        });
        setFieldStates(initial);
    }, [data]);

    const updateField = (field_id: number, key: keyof Omit<FieldState, "field_id">, value: any) => {
        setFieldStates(prev => {
            const updated = { ...prev[field_id], [key]: value };
            if (key === "is_complete") {
                updated.need_validation = !value;
                if (value === true) updated.has_issue = false;
            }
            if (key === "has_issue" && value === true) {
                updated.is_complete = false;
                updated.need_validation = true;
            }
            return { ...prev, [field_id]: updated };
        });
    };

    const handleSave = async () => {
        try {
            const field_statuses = Object.values(fieldStates).map(f => ({
                field_id: f.field_id,
                has_issue: f.has_issue,
                is_complete: f.is_complete,
                need_validation: f.need_validation,
                remarks: f.remarks,
            }));

            const res = await bulkUpsert({
                variables: { agency_id: parseInt(agencyId), field_statuses },
            });
            const result = res.data?.bulk_upsert_agency_field_status;
            if (result?.status_code === 200 || result?.status_code === 201) {
                showSuccess(result.message || "Agency info saved successfully");
            } else {
                showError(result?.message || "Something went wrong");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    if (loading) return <div className="p-6"><Loader /></div>;

    if (!fields.length) {
        return (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                No field statuses found. Please sync data first.
            </div>
        );
    }

    return (
        <div className="p-6 flex flex-col gap-4">
            {fields.map(field => {
                const state = fieldStates[field.field_id];
                if (!state) return null;
                return (
                    <div key={field.field_id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-5">
                        <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                                <h5 className="text-sm font-bold text-gray-900 dark:text-white m-0">
                                    {field.field_name}
                                </h5>
                                {field.field_description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 m-0">
                                        {field.field_description}
                                    </p>
                                )}
                            </div>
                            {/* Checkboxes */}
                            <div className="flex items-center gap-5 shrink-0">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 cursor-pointer accent-red-500"
                                        checked={state.has_issue}
                                        onChange={e => updateField(field.field_id, "has_issue", e.target.checked)}
                                    />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Issue</span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 cursor-pointer accent-green-500"
                                        checked={state.is_complete}
                                        onChange={e => updateField(field.field_id, "is_complete", e.target.checked)}
                                    />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Completed</span>
                                </label>
                            </div>
                        </div>
                        {/* Remarks */}
                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white resize-y text-sm"
                            rows={2}
                            placeholder="Add remarks..."
                            value={state.remarks}
                            onChange={e => updateField(field.field_id, "remarks", e.target.value)}
                        />
                    </div>
                );
            })}

            <div className="flex justify-end pt-2">
                <button
                    type="button"
                    disabled={saving}
                    onClick={handleSave}
                    className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm disabled:opacity-60"
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </div>
    );
};

export default AgencyInfoTab;
